// PhotoViewer — substitui o shim smartgis-components/build/ImageViewer.
// Renderiza thumbs em grid, abre lightbox Mantine Modal ao clicar, com
// navegação prev/next e contador.
//
// Props mantém a API do ImageViewer pra ser drop-in replacement:
//   <PhotoViewer fotos={fotos} thumbs={thumbs} dates={dates} className="..." />
import { useState } from 'react';
import { Box, Modal, Image, ActionIcon, Group, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

export interface PhotoViewerProps {
  fotos: string[];
  thumbs?: string[];
  dates?: string[];
  className?: string;
}

export default function PhotoViewer({ fotos = [], thumbs = [], dates = [], className }: PhotoViewerProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (fotos.length === 0) return null;

  const close = () => setOpenIndex(null);
  const prev = () => setOpenIndex((i) => (i == null ? null : Math.max(0, i - 1)));
  const next = () => setOpenIndex((i) => (i == null ? null : Math.min(fotos.length - 1, i + 1)));

  const renderThumbs = thumbs.length > 0 ? thumbs : fotos;

  return (
    <>
      <Box className={className} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {renderThumbs.map((src, i) => (
          <Box
            key={`${src}-${i}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setOpenIndex(i)}
          >
            <Image
              src={src}
              alt={dates[i] || `Foto ${i + 1}`}
              w={150}
              h={100}
              fit="cover"
              radius="sm"
              style={{ border: '1px solid var(--mantine-color-gray-3)' }}
            />
            {dates[i] && (
              <Text size="xs" c="dimmed" ta="center" mt={2}>{dates[i]}</Text>
            )}
          </Box>
        ))}
      </Box>

      <Modal
        opened={openIndex !== null}
        onClose={close}
        size="auto"
        centered
        padding="md"
        withCloseButton
        title={openIndex !== null && dates[openIndex] ? dates[openIndex] : null}
      >
        {openIndex !== null && (
          <Box>
            <Image
              src={fotos[openIndex]}
              alt={`Foto ${openIndex + 1}`}
              fit="contain"
              style={{ maxWidth: '90vw', maxHeight: '75vh' }}
            />
            <Group justify="center" mt="md" gap="md">
              <ActionIcon
                variant="default"
                size="lg"
                onClick={prev}
                disabled={openIndex === 0}
                aria-label="Foto anterior"
              >
                <IconChevronLeft size={20} />
              </ActionIcon>
              <Text size="sm" c="dimmed">{openIndex + 1} / {fotos.length}</Text>
              <ActionIcon
                variant="default"
                size="lg"
                onClick={next}
                disabled={openIndex === fotos.length - 1}
                aria-label="Próxima foto"
              >
                <IconChevronRight size={20} />
              </ActionIcon>
            </Group>
          </Box>
        )}
      </Modal>
    </>
  );
}
